/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// material
import { Group, TextField, Control, Switch, InlineMessage, Link, sendErrorMessage } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionDescription from 'material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription';
import ConfigController from 'material/expandablesection/controllers/configcontroller/configcontroller';
import RatingsCountLabel from 'pages/dashboard/ratings/ratingsmanager/configuration/ratingsgeneralsection/ratingscountlabel/ratingscountlabel';
// actions
import { saveRatingsGeneral } from 'state/actions/ratingsState/configStatus';
// utils
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/ratings/ratingsmanager/configuration/ratingsgeneralsection/ratingsgeneralsection_styles';


const RatingsGeneralSection = (props) => {

    // action dispatch
    const dispatch = useDispatch();

    // redux state
    const siteName = useSelector(({ ratingsState }) => ratingsState.ratingsStatus.selectedSite.name);
    const configStatus = useSelector(({ ratingsState }) => ratingsState.configStatus.configuration);
    const savingGeneral = useSelector(({ ratingsState }) => ratingsState.configStatus.configuration.savingGeneral);
    const fontConfiguration = configStatus.fontConfiguration;
    // component state
    const [isOpened, setIsOpened] = useState(false);

    const [title, updateTitle] = useState(configStatus.title.value);
    const [firstRatingTitle, updateFirstRatingTitle] = useState(configStatus.firstRatingTitle.value);
    const [thankYouMessage, updateThankYouMessage] = useState(configStatus.thankYouMessage.value);
    const [zeroRatingLabel, updateZeroRatingLabel] = useState(configStatus.zeroRatingLabel.value);
    const [oneRatingLabel, updateOneRatingLabel] = useState(configStatus.oneRatingLabel.value);
    const [multipleRatingsLabel, updateMultipleRatingsLabel] = useState(configStatus.multipleRatingsLabel.value);

    const [titleValid, updateTitleValid] = useState(configStatus.title.valid);
    const [firstRatingTitleValid, updateFirstRatingTitleValid] = useState(configStatus.firstRatingTitle.valid);
    const [thankYouMessageValid, updateThankYouMessageValid] = useState(configStatus.thankYouMessage.valid);
    const [zeroRatingLabelValid, updateZeroRatingLabelValid] = useState(configStatus.zeroRatingLabel.valid);
    const [oneRatingLabelValid, updateOneRatingLabelValid] = useState(configStatus.oneRatingLabel.valid);
    const [multipleRatingsLabelValid, updateMultipleRatingsLabelValid] = useState(configStatus.multipleRatingsLabel.valid);

    const [enablePageId, updateEnablePageId] = useState(configStatus.enablePageId);
    const [fontSize, updateFontSize] = useState(fontConfiguration.size);
    const [fontWeight, updateFontWeight] = useState(fontConfiguration.weight);

    const handleCancel = () => {
        updateTitle(configStatus.title.value)
        updateFirstRatingTitle(configStatus.firstRatingTitle.value)
        updateThankYouMessage(configStatus.thankYouMessage.value)
        updateZeroRatingLabel(configStatus.zeroRatingLabel.value)
        updateOneRatingLabel(configStatus.oneRatingLabel.value)
        updateMultipleRatingsLabel(configStatus.multipleRatingsLabel.value)
        updateTitleValid(configStatus.title.valid)
        updateFirstRatingTitleValid(configStatus.firstRatingTitle.valid)
        updateThankYouMessageValid(configStatus.thankYouMessage.valid)
        updateZeroRatingLabelValid(configStatus.zeroRatingLabel.valid)
        updateOneRatingLabelValid(configStatus.oneRatingLabel.valid)
        updateMultipleRatingsLabelValid(configStatus.multipleRatingsLabel.valid)
        updateEnablePageId(configStatus.enablePageId)
        updateFontSize(fontConfiguration.size)
        updateFontWeight(fontConfiguration.weight)
    };

    const handleSave = () => {
        if (titleValid && firstRatingTitleValid && thankYouMessageValid
            && zeroRatingLabelValid && oneRatingLabelValid && multipleRatingsLabelValid ){
            dispatch(saveRatingsGeneral(configStatus.uuid, saveData(), setIsOpened))
        } else {
            return dispatch(sendErrorMessage('Please update invalid fields.'))
        }
    };

    const saveData = () => ({
        title: title.trim(),
        firstRatingTitle: firstRatingTitle.trim(),
        thankYouMessage: thankYouMessage.trim(),
        enablePageId,
        zeroRatingLabel: zeroRatingLabel.trim(),
        oneRatingLabel: oneRatingLabel.trim(),
        multipleRatingsLabel: multipleRatingsLabel.trim(),
        fontConfiguration: {
            uuid: fontConfiguration.uuid,
            size: fontSize,
            weight: fontWeight
        }
    })

    const pageIDCopy = <span>Allows to ensure that your comments surface in a persistent manner on a given article under different URLs (e.g. when the URL of an article changes automatically over time).</span>;

    const titleTest = /^(?!\s)[!@#$%^&*(){}[\]<>?/|.:;_A-Za-z0-9'\-\s]{1,40}$/;
    const thankYouTest = /^(?!\s)[!@#$%^&*(){}[\]<>?/|.:;_A-Za-z0-9'\-\s]{1,255}$/;
    const labelTest = /^(?!\s)[!@#$%^&*(){}[\]<>?/|.:;_A-Za-z0-9'\-\s]{1,20}$/;

    const validateTitle = e => updateTitleValid(titleTest.test(e.target.value));
    const validateFirstTitle = e => updateFirstRatingTitleValid(titleTest.test(e.target.value));
    const validateThankYouMessage = e => updateThankYouMessageValid(thankYouTest.test(e.target.value));
    const validateZeroRatingLabel = e => updateZeroRatingLabelValid(labelTest.test(e.target.value));
    const validateOneRatingLabel = e => updateOneRatingLabelValid(labelTest.test(e.target.value));
    const validateMultipleRatingsLabel = e => updateMultipleRatingsLabelValid(labelTest.test(e.target.value));

    const disableSave = enablePageId === configStatus.enablePageId
            && firstRatingTitle === configStatus.firstRatingTitle.value
            && title === configStatus.title.value
            && thankYouMessage === configStatus.thankYouMessage.value
            && fontSize === fontConfiguration.size
            && fontWeight === fontConfiguration.weight
            && zeroRatingLabel === configStatus.zeroRatingLabel.value
            && oneRatingLabel === configStatus.oneRatingLabel.value
            && multipleRatingsLabel === configStatus.multipleRatingsLabel.value;

    const labelsData = ({
        zero: {
            value: zeroRatingLabel,
            valid: zeroRatingLabelValid,
            updateValue: updateZeroRatingLabel,
            updateValid: validateZeroRatingLabel
        },
        one: {
            value: oneRatingLabel,
            valid: oneRatingLabelValid,
            updateValue: updateOneRatingLabel,
            updateValid: validateOneRatingLabel
        },
        multiple: {
            value: multipleRatingsLabel,
            valid: multipleRatingsLabelValid,
            updateValue: updateMultipleRatingsLabel,
            updateValid: validateMultipleRatingsLabel
        }
    });

    const controller =  (
        <ConfigController
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            handleCancel={handleCancel}
            handleSave={handleSave}
            disableSave={disableSave}
            title={'General'}
            description={<SectionDescription/>}
            processing={savingGeneral}
        />
    );

    return (
        <ExpandableSection
            className={`ratingsgeneralsection ${props.classes.ratingsgeneralsection}`}
            controller={controller}
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            handleCancel={handleCancel}
            handleSave={handleSave}
            disableSave={disableSave}
            processing={savingGeneral}
        >
            <SectionCard>
                <SectionGroup>
                    <SectionGroupDescription
                        title={'Website Name'}
                    />
                    <TextField
                        type={'text'}
                        readOnly={true}
                        value={siteName}
                        valid={true}
                        placeholder={'Name'}
                    />
                </SectionGroup>
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Enable External Page ID Functionality'}
                        description={pageIDCopy}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateEnablePageId(!enablePageId)}
                        on={enablePageId}
                        variant='secondary'
                    />
                </SectionGroup>
                <InlineMessage
                    key={'meow'}
                    display={true}
                    name={'headercode'}
                    variant={'neutral'}
                    icon={'exclamation-triangle'}
                    title={null}
                    message={<span>Enabling or disabling External Page ID affects the embed codes for the Ratings Unit and for the Ratings Counter.</span>}
                    close={false}
                    handleClose={null} />
            </SectionCard>
            <SectionCard>
                <SectionGroup>
                    <SectionGroupDescription
                        title={'Ratings Unit Title (First Rating, User Prompt)'}
                    />
                    <TextField
                        type={'text'}
                        required={true}
                        name={'firstRatingTitle'}
                        value={firstRatingTitle}
                        valid={firstRatingTitleValid}
                        handleBlur={validateFirstTitle}
                        handleChange={e => updateFirstRatingTitle(e.target.value)}
                        placeholder={'Name'}
                    />
                </SectionGroup>
                <SectionGroup>
                    <SectionGroupDescription
                        title={'Ratings Unit Title (After First Rating, User Prompt)'}
                    />
                    <TextField
                        type={'text'}
                        required={true}
                        name={'ratingUnitTitle'}
                        value={title}
                        valid={titleValid}
                        handleBlur={validateTitle}
                        handleChange={e => updateTitle(e.target.value)}
                        placeholder={'Name'}
                    />
                </SectionGroup>
                <SectionGroup>
                    <SectionGroupDescription
                        title={'Ratings Unit Thank You Message (After Rating Is Submitted)'}
                    />
                    <TextField
                        type={'text'}
                        required={true}
                        name={'ratingThankYouMessage'}
                        value={thankYouMessage}
                        valid={thankYouMessageValid}
                        handleBlur={validateThankYouMessage}
                        handleChange={e => updateThankYouMessage(e.target.value)}
                        placeholder={'Name'}
                    />
                </SectionGroup>
            </SectionCard>
            <SectionCard>
                <SectionGroup>
                    <SectionGroupDescription
                        title='Unit Label'
                        description={<div>Customize the <strong>label</strong> that appears to the right of the Ratings Unit title:</div>}
                    />
                    <RatingsCountLabel
                        type={'counterConfiguration'}
                        data={labelsData}
                    />
                </SectionGroup>
            </SectionCard>
            <SectionCard>
                <SectionGroupDescription
                    title={'Font Configurations'}
                    description={'Font settings for better accessibility'}
                />
                <SectionGroup secondary>
                    <SectionGroupDescription title='Font size' />
                    <Group name={'controls'}>
                        <Control
                            text={'Regular'}
                            value={'REGULAR'}
                            selected={fontSize === 'REGULAR'}
                            handleSelect={updateFontSize}
                            variant={'secondary'}
                            addCheckWhenSelected={true}
                        />
                        <Control
                            text={'Comfortable'}
                            value={'COMFORTABLE'}
                            selected={fontSize === 'COMFORTABLE'}
                            handleSelect={updateFontSize}
                            variant={'secondary'}
                            addCheckWhenSelected={true}
                        />
                        <Control
                            text={'Accessible'}
                            value={'ACCESSIBLE'}
                            selected={fontSize === 'ACCESSIBLE'}
                            handleSelect={updateFontSize}
                            variant={'secondary'}
                            addCheckWhenSelected={true}
                        />
                    </Group>
                </SectionGroup>
                <SectionGroup secondary>
                    <SectionGroupDescription title='Font weight' />
                    <Group name={'controls'}>
                        <Control
                            text={'Regular'}
                            value={'REGULAR'}
                            selected={fontWeight === 'REGULAR'}
                            handleSelect={updateFontWeight}
                            variant={'secondary'}
                            addCheckWhenSelected={true}
                        />
                        <Control
                            text={'Medium'}
                            value={'MEDIUM'}
                            selected={fontWeight === 'MEDIUM'}
                            handleSelect={updateFontWeight}
                            variant={'secondary'}
                            addCheckWhenSelected={true}
                        />
                    </Group>
                </SectionGroup>
            </SectionCard>

        </ExpandableSection>)
};

export default injectSheet(styles)(RatingsGeneralSection)
